import type { v2 } from '@deque/billing-service-client';
import { apiRequest } from '../../../api-client';

/**
 * Walnut wraps the billing-service v2 user with `stripe_name` (the raw
 * value as seen in Stripe) on top of the overridden `name` field (the
 * Walnut-derived display name). Forms that need to confirm the Stripe-side
 * billing name (e.g. Purchase) should read `stripe_name`.
 */
export type AxeAccountV2MeUser = v2.User & {
  stripe_name?: string | null;
  /**
   * Empty when the caller belongs to no teams, has no enterprise, or the
   * lookup degraded.
   */
  teams?: Pick<v2.Team, 'id' | 'name'>[];
};
export type MeSubscriptionResponse =
  | v2.UserSubscription
  | v2.EnterpriseSubscription;

export interface UpdateMeParams {
  firstName: string;
  lastName: string;
}

export default {
  /**
   * Get the billing data for the logged-in user.
   */
  get(token: string): Promise<AxeAccountV2MeUser> {
    return apiRequest<AxeAccountV2MeUser>({
      endpoint: '/api/billing/v2/me',
      method: 'GET',
      token
    });
  },
  /**
   * Update the billing user's first/last name for the logged-in user. The
   * server fans the change out to Keycloak, the Walnut DB, and Stripe.
   */
  update(token: string, params: UpdateMeParams) {
    return apiRequest({
      method: 'PUT',
      endpoint: '/api/billing/v2/me',
      token,
      body: params
    });
  },
  /**
   * Returns whether or not the active user is an enterprise admin for the
   * provided enterpriseId
   */
  async isEnterpriseAdmin(
    token: string,
    enterpriseId: string
  ): Promise<boolean> {
    const { is_admin } = await apiRequest<{ is_admin: boolean }>({
      endpoint: `/api/billing/v2/me/enterprises/${enterpriseId}/is_admin`,
      method: 'GET',
      token
    });

    return is_admin;
  },

  /* Return the invitations associated for the logged in user */
  async getUserInvitations(
    token: string,
    isAccepted?: boolean
  ): Promise<v2.Invitation[]> {
    const invitations = await apiRequest<v2.Invitation[]>({
      endpoint: `/api/billing/v2/me/invitations?is_accepted=${isAccepted}`,
      method: 'GET',
      token
    });
    return invitations;
  },

  /* Rejects the user invitation with the `invitation_id` */
  rejectUserInvitation(invitationId: string, token: string): Promise<unknown> {
    return apiRequest({
      endpoint: `/api/billing/v2/me/invitations/${invitationId}`,
      method: 'DELETE',
      token
    });
  },

  /** Create a trial subscription for the logged-in user's billing account. */
  async createTrialSubscription(
    productId: string,
    token: string
  ): Promise<MeSubscriptionResponse> {
    const subscription = await apiRequest<MeSubscriptionResponse>({
      method: 'POST',
      endpoint: `/api/billing/v2/me/trials/${productId}`,
      token
    });
    return subscription;
  },

  /** Create a free subscription for the logged-in user's billing account. */
  async createFreeSubscription(
    productId: string,
    token: string
  ): Promise<MeSubscriptionResponse> {
    const subscription = await apiRequest<MeSubscriptionResponse>({
      method: 'POST',
      endpoint: `/api/billing/v2/me/free-subscriptions/${productId}`,
      token
    });
    return subscription;
  }
};
